<template>
  <div class="p-4 sm:p-6">
    <!-- Welcome Card -->
    <div class="mb-6 sm:mb-8">
      <div class="relative overflow-hidden bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 dark:from-blue-600 dark:via-blue-700 dark:to-indigo-800 rounded-2xl shadow-xl">
        <!-- Decorative Background Pattern -->
        <div class="absolute inset-0 opacity-10">
          <svg class="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="20" cy="20" r="1.5" fill="white"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)"/>
          </svg>
        </div>

        <!-- Content -->
        <div class="relative px-6 py-8 sm:px-8 sm:py-10 md:px-10 md:py-12">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <!-- Welcome Text -->
            <div class="flex-1">
              <div class="flex items-center gap-3 mb-2">
                <div>
                  <h1 class="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                    Welcome back, {{ adminName }}! 👋
                  </h1>
                  <p class="text-blue-100 text-sm sm:text-base mt-1">
                    {{ currentGreeting }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Quick Stats Badge -->
            <div class="flex-shrink-0">
              <div class="inline-flex items-center gap-2 px-4 py-2.5 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
                <div class="flex items-center gap-2">
                  <div class="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span class="text-white text-sm font-medium">System Active</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Info Row -->
          <div class="mt-6 pt-6 border-t border-white/20">
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div class="flex items-center gap-3">
                <div class="flex-shrink-0 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-white">
                    <path d="M12.75 12.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM7.5 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM8.25 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM9.75 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM10.5 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM12.75 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM14.25 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM15 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM16.5 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM15 12.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM16.5 13.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" />
                    <path fill-rule="evenodd" d="M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75Zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5Z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p class="text-blue-100 text-xs">Today</p>
                  <p class="text-white font-semibold text-sm">{{ currentDate }}</p>
                </div>
              </div>

              <div class="flex items-center gap-3">
                <div class="flex-shrink-0 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-white">
                    <path fill-rule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 0 0 0-1.5h-3.75V6Z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p class="text-blue-100 text-xs">Time</p>
                  <p class="text-white font-semibold text-sm">{{ currentTime }}</p>
                </div>
              </div>

              <div class="flex items-center gap-3 col-span-2 sm:col-span-1">
                <div class="flex-shrink-0 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-white">
                    <path fill-rule="evenodd" d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 00-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 002.682 2.282 16.975 16.975 0 001.145.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p class="text-blue-100 text-xs">Role</p>
                  <p class="text-white font-semibold text-sm">Administrator</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Per-Brand Stats Sections -->
    <div v-for="brand in filteredBrandStats" :key="brand.brand_id" class="mb-8">
      <!-- Brand Section Header -->
      <div class="flex items-center gap-3 mb-4">
        <h2 class="text-xl font-bold text-gray-800 dark:text-white">
          {{ brand.brand_name }}
        </h2>
      </div>

      <!-- Stats Cards - 4 Column Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <!-- Total Customers Card -->
        <div class="border border-gray-200 dark:border-gray-700 bg-white dark:bg-black rounded-xl shadow-lg h-full">
          <!-- Loading Skeleton -->
          <div v-if="loading" class="p-6 h-full flex flex-col">
            <div class="flex items-start justify-between mb-12">
              <div class="flex-1 space-y-3">
                <div class="h-4 w-32 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse"></div>
                <div class="h-3 w-40 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse"></div>
              </div>
              <div class="w-14 h-14 bg-gray-200 dark:bg-gray-800 rounded-xl skeleton-pulse"></div>
            </div>
            <div class="mt-auto">
              <div class="h-10 w-20 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse"></div>
            </div>
          </div>

          <!-- Actual Content -->
          <div v-else class="p-6 h-full flex flex-col">
            <div class="flex items-start justify-between mb-12">
              <div class="flex-1">
                <p
                  class="text-sm font-semibold uppercase tracking-wide mb-1"
                  :style="{ color: getBrandColor(brand.brand_id) }"
                >
                  Total Customers
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  All registered customers
                </p>
              </div>
              <div
                class="p-3 rounded-xl flex-shrink-0"
                :style="{ backgroundColor: getBrandAccent(brand.brand_id) + '20' }"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-6" :style="{ color: getBrandAccent(brand.brand_id) }">
                  <path d="M4.5 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM14.25 8.625a3.375 3.375 0 1 1 6.75 0 3.375 3.375 0 0 1-6.75 0ZM1.5 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM17.25 19.128l-.001.144a2.25 2.25 0 0 1-.233.96 10.088 10.088 0 0 0 5.06-1.01.75.75 0 0 0 .42-.643 4.875 4.875 0 0 0-6.957-4.611 8.586 8.586 0 0 1 1.71 5.157v.003Z" />
                </svg>
              </div>
            </div>
            <div class="mt-auto">
              <p
                class="text-4xl font-medium dark:text-white"
                :style="{ color: getBrandColor(brand.brand_id) }"
              >{{ brand.total_customers }}</p>
            </div>
          </div>
        </div>

        <!-- Pending Tires Card -->
        <div class="border border-gray-200 dark:border-gray-700 bg-white dark:bg-black rounded-xl shadow-lg h-full">
          <!-- Loading Skeleton -->
          <div v-if="loading" class="p-6 h-full flex flex-col">
            <div class="flex items-start justify-between mb-12">
              <div class="flex-1 space-y-3">
                <div class="h-4 w-36 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse"></div>
                <div class="h-3 w-44 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse"></div>
              </div>
              <div class="w-14 h-14 bg-gray-200 dark:bg-gray-800 rounded-xl skeleton-pulse"></div>
            </div>
            <div class="mt-auto">
              <div class="h-10 w-20 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse"></div>
            </div>
          </div>

          <!-- Actual Content -->
          <div v-else class="p-6 h-full flex flex-col">
            <div class="flex items-start justify-between mb-12">
              <div class="flex-1">
                <p
                  class="text-sm font-semibold uppercase tracking-wide mb-1"
                  :style="{ color: getBrandColor(brand.brand_id) }"
                >
                  Pending Tires
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Tires awaiting approval
                </p>
              </div>
              <div
                class="p-3 rounded-xl flex-shrink-0"
                :style="{ backgroundColor: getBrandAccent(brand.brand_id) + '20' }"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-6" :style="{ color: getBrandAccent(brand.brand_id) }">
                  <path fill-rule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 0 0 0-1.5h-3.75V6Z" clip-rule="evenodd" />
                </svg>
              </div>
            </div>
            <div class="mt-auto">
              <p
                class="text-4xl font-medium dark:text-white"
                :style="{ color: getBrandColor(brand.brand_id) }"
              >{{ brand.pending_tires }}</p>
            </div>
          </div>
        </div>

        <!-- Approved Tires Card -->
        <div class="border border-gray-200 dark:border-gray-700 bg-white dark:bg-black rounded-xl shadow-lg h-full">
          <!-- Loading Skeleton -->
          <div v-if="loading" class="p-6 h-full flex flex-col">
            <div class="flex items-start justify-between mb-12">
              <div class="flex-1 space-y-3">
                <div class="h-4 w-36 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse"></div>
                <div class="h-3 w-40 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse"></div>
              </div>
              <div class="w-14 h-14 bg-gray-200 dark:bg-gray-800 rounded-xl skeleton-pulse"></div>
            </div>
            <div class="mt-auto">
              <div class="h-10 w-20 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse"></div>
            </div>
          </div>

          <!-- Actual Content -->
          <div v-else class="p-6 h-full flex flex-col">
            <div class="flex items-start justify-between mb-12">
              <div class="flex-1">
                <p
                  class="text-sm font-semibold uppercase tracking-wide mb-1"
                  :style="{ color: getBrandColor(brand.brand_id) }"
                >
                  Approved Tires
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Tires that are approved
                </p>
              </div>
              <div
                class="p-3 rounded-xl flex-shrink-0"
                :style="{ backgroundColor: getBrandAccent(brand.brand_id) + '20' }"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-6" :style="{ color: getBrandAccent(brand.brand_id) }">
                  <path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clip-rule="evenodd" />
                </svg>
              </div>
            </div>
            <div class="mt-auto">
              <p
                class="text-4xl font-medium dark:text-white"
                :style="{ color: getBrandColor(brand.brand_id) }"
              >{{ brand.approved_tires }}</p>
            </div>
          </div>
        </div>

        <!-- Pending Claims Card -->
        <div class="border border-gray-200 dark:border-gray-700 bg-white dark:bg-black rounded-xl shadow-lg h-full">
          <!-- Loading Skeleton -->
          <div v-if="loading" class="p-6 h-full flex flex-col">
            <div class="flex items-start justify-between mb-12">
              <div class="flex-1 space-y-3">
                <div class="h-4 w-32 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse"></div>
                <div class="h-3 w-44 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse"></div>
              </div>
              <div class="w-14 h-14 bg-gray-200 dark:bg-gray-800 rounded-xl skeleton-pulse"></div>
            </div>
            <div class="mt-auto">
              <div class="h-10 w-20 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse"></div>
            </div>
          </div>

          <!-- Actual Content -->
          <div v-else class="p-6 h-full flex flex-col">
            <div class="flex items-start justify-between mb-12">
              <div class="flex-1">
                <p
                  class="text-sm font-semibold uppercase tracking-wide mb-1"
                  :style="{ color: getBrandColor(brand.brand_id) }"
                >
                  Pending Claims
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Claims requiring review
                </p>
              </div>
              <div
                class="p-3 rounded-xl flex-shrink-0"
                :style="{ backgroundColor: getBrandAccent(brand.brand_id) + '20' }"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-6" :style="{ color: getBrandAccent(brand.brand_id) }">
                  <path fill-rule="evenodd" d="M7.502 6h7.128A3.375 3.375 0 0 1 18 9.375v9.375a3 3 0 0 0 3-3V6.108c0-1.505-1.125-2.811-2.664-2.94a48.972 48.972 0 0 0-.673-.05A3 3 0 0 0 15 1.5h-1.5a3 3 0 0 0-2.663 1.618c-.225.015-.45.032-.673.05C8.662 3.295 7.554 4.542 7.502 6ZM13.5 3A1.5 1.5 0 0 0 12 4.5h4.5A1.5 1.5 0 0 0 15 3h-1.5Z" clip-rule="evenodd" />
                  <path fill-rule="evenodd" d="M3 9.375C3 8.339 3.84 7.5 4.875 7.5h9.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-9.75A1.875 1.875 0 0 1 3 20.625V9.375ZM6 12a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 .75.75v.008a.75.75 0 0 1-.75.75H6.75a.75.75 0 0 1-.75-.75V12Zm2.25 0a.75.75 0 0 1 .75-.75h3.75a.75.75 0 0 1 0 1.5H9a.75.75 0 0 1-.75-.75ZM6 15a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 .75.75v.008a.75.75 0 0 1-.75.75H6.75a.75.75 0 0 1-.75-.75V15Zm2.25 0a.75.75 0 0 1 .75-.75h3.75a.75.75 0 0 1 0 1.5H9a.75.75 0 0 1-.75-.75ZM6 18a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 .75.75v.008a.75.75 0 0 1-.75.75H6.75a.75.75 0 0 1-.75-.75V18Zm2.25 0a.75.75 0 0 1 .75-.75h3.75a.75.75 0 0 1 0 1.5H9a.75.75 0 0 1-.75-.75Z" clip-rule="evenodd" />
                </svg>
              </div>
            </div>
            <div class="mt-auto">
              <p
                class="text-4xl font-medium dark:text-white"
                :style="{ color: getBrandColor(brand.brand_id) }"
              >{{ brand.pending_claims }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useBrand } from '@/composables/brand/useBrand'
import { useAuthStore } from '@/stores/auth'
import { adminTireService, type BrandStats } from '@/services/api/adminTireService'
import { getBrandById } from '@/config/brands'

// Composables
const { brandColors } = useBrand()
const authStore = useAuthStore()

// State
const loading = ref(false)
const brandStats = ref<BrandStats[]>([])
const currentTime = ref('')
const currentDate = ref('')

// Computed
const adminName = computed(() => {
  const user = authStore.currentUser
  if (user?.last_name) {
    return user.last_name
  }
  return 'Admin'
})

const filteredBrandStats = computed(() => {
  const selectedBrandId = authStore.adminBrandId
  if (!selectedBrandId) {
    return brandStats.value
  }

  const matchingBrandStats = brandStats.value.filter(brand => brand.brand_id === selectedBrandId)
  return matchingBrandStats.length > 0 ? matchingBrandStats : brandStats.value
})

const currentGreeting = computed(() => {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning! Ready to manage your dashboard.'
  if (hour < 18) return 'Good afternoon! Here\'s your overview for today.'
  return 'Good evening! Check out today\'s summary.'
})

// Update time and date
const updateDateTime = () => {
  const now = new Date()
  currentTime.value = now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })
  currentDate.value = now.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

let timeInterval: number | undefined

// Helper functions for brand-specific colors
const getBrandColor = (brandId: number) => {
  const brand = getBrandById(brandId)
  return brand?.color || brandColors.value.primary
}

const getBrandAccent = (brandId: number) => {
  const brand = getBrandById(brandId)
  return brand?.accentColor || brandColors.value.accent
}

// Methods
async function loadDashboardStats() {
  loading.value = true
  try {
    const response = await adminTireService.getDashboardStats()
    brandStats.value = response.data
  } catch (error) {
    console.error('Error loading dashboard stats:', error)
  } finally {
    loading.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadDashboardStats()
  updateDateTime()
  // Update time every second
  timeInterval = window.setInterval(updateDateTime, 1000)
})

onUnmounted(() => {
  if (timeInterval) {
    clearInterval(timeInterval)
  }
})
</script>

<style scoped>
@keyframes skeleton-shimmer {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}

.skeleton-pulse {
  background: linear-gradient(
    90deg,
    rgb(229 231 235) 0%,
    rgb(209 213 219) 50%,
    rgb(229 231 235) 100%
  );
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s ease-in-out infinite;
}

.dark .skeleton-pulse {
  background: linear-gradient(
    90deg,
    rgb(55 65 81) 0%,
    rgb(75 85 99) 50%,
    rgb(55 65 81) 100%
  );
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s ease-in-out infinite;
}
</style>
